import { useCallback, useState } from "react";
import JSZip from "jszip";
import { Klydo } from "../../../api/klydo";
import { constructCloudinaryFrameUrl } from "../../../utils/util";

const useDownloadCollectionFrames = () => {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadFrames = useCallback(
    async (klydos: Klydo[], collectionName: string) => {
      if (!klydos.length) return;

      setIsDownloading(true);
      try {
        const zip = new JSZip();

        const fetchResults = await Promise.allSettled(
          klydos.map(async (klydo) => {
            const duration = klydo.metadata?.klydoDurationInSeconds || 10;
            const randomOffset = Math.floor(Math.random() * duration);
            const frameUrl = constructCloudinaryFrameUrl(
              klydo.loopUrl,
              "jpg",
              `so_${randomOffset}`,
            );

            if (!frameUrl) {
              throw new Error(`Invalid frame URL for ${klydo.name}`);
            }

            const response = await fetch(frameUrl);
            if (!response.ok) {
              throw new Error(`Failed to fetch frame for ${klydo.name}`);
            }
            const blob = await response.blob();
            const safeName =
              klydo.name.replace(/[^a-zA-Z0-9_-]/g, "_") || "klydo";
            return { filename: `${safeName}_${klydo.id}.jpg`, blob };
          }),
        );

        let addedFilesCount = 0;
        for (const result of fetchResults) {
          if (result.status === "fulfilled") {
            zip.file(result.value.filename, result.value.blob);
            addedFilesCount += 1;
          }
        }

        if (addedFilesCount === 0) {
          throw new Error("No frames could be fetched for this collection");
        }

        const safeCollectionName = (collectionName || "collection").replace(
          /[^a-zA-Z0-9_-]/g,
          "_",
        );
        const zipBlob = await zip.generateAsync({ type: "blob" });
        const objectUrl = URL.createObjectURL(zipBlob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = `${safeCollectionName}-frames.zip`;
        link.click();
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error("Failed to download collection frames:", error);
      } finally {
        setIsDownloading(false);
      }
    },
    [],
  );

  return { downloadFrames, isDownloading };
};

export default useDownloadCollectionFrames;
